import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card } from '@mui/material';
import DataTable from '../../../sharedModules/dataTable';
import { useCollectionEmailSends } from '../../emailHistory/hooks/useCollectionEmailSends';
import { CollectionEmailSendListItem, CollectionEmailType } from '../../emailHistory/model';
import { SourceChip, StatusChip, ToneChip } from '../../emailHistory/components/EmailHistoryChips';
import EmailHistoryDetailModal from '../../emailHistory/components/EmailHistoryDetailModal';

interface Props {
  location: string;
  clientId: number;
  emailType?: CollectionEmailType;
}

const PER_PAGE = 10;

const toneColumn = {
  name: 'Tone',
  selector: 'tone',
  cell: (row: CollectionEmailSendListItem) => <ToneChip row={row} />,
  width: '160px',
};

const buildColumns = (emailType: CollectionEmailType) => [
  // Statement sends never carry a tone, so the column would be a constant under a header that
  // already says statement.
  ...(emailType === 'collection' ? [toneColumn] : []),
  {
    name: 'Status',
    selector: 'status',
    cell: (row: CollectionEmailSendListItem) => <StatusChip status={row.status} />,
    width: '110px',
  },
  {
    name: 'Source',
    selector: 'send_source',
    cell: (row: CollectionEmailSendListItem) => <SourceChip row={row} />,
    width: '120px',
  },
  {
    name: 'Sent At',
    selector: 'delivered_at',
    format: (row: CollectionEmailSendListItem) => row.delivered_at ?? '—',
    width: '200px',
  },
];

const ClientEmailHistoryTable: React.FC<Props> = ({ location, clientId, emailType = 'collection' }) => {
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Stable identity: DataTable regenerates every column id when this prop changes.
  const columns = useMemo(() => buildColumns(emailType), [emailType]);

  useEffect(() => {
    setPage(1);
    setSelectedId(null);
  }, [location, clientId, emailType]);

  const { data, isLoading, isError } = useCollectionEmailSends({
    location,
    collections_client_id: clientId,
    page,
    per_page: PER_PAGE,
    email_type: emailType,
  });

  const rows = data?.data?.result ?? [];
  const totalRecords = data?.data?.total_records ?? 0;

  return (
    <>
      {isError && <Box sx={{ p: 2, color: 'error.main' }}>Failed to load email history.</Box>}
      <Card>
        <DataTable
          keyField="id"
          columns={columns}
          data={rows}
          progressPending={isLoading}
          pagination
          paginationServer
          paginationPerPage={PER_PAGE}
          paginationRowsPerPageOptions={[PER_PAGE]}
          paginationTotalRows={totalRecords}
          onChangePage={(nextPage: number) => setPage(nextPage)}
          highlightOnHover
          striped
          noHeader
          pointerOnHover
          onRowClicked={(row: CollectionEmailSendListItem) => setSelectedId(row.id)}
          noDataComponent={<Box sx={{ p: 3, color: 'text.secondary' }}>No emails sent to this client yet.</Box>}
        />
      </Card>
      <EmailHistoryDetailModal
        open={selectedId !== null}
        emailSendId={selectedId}
        onClose={() => setSelectedId(null)}
        emailType={emailType}
        location={location}
      />
    </>
  );
};

export default ClientEmailHistoryTable;
